import { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      return response.data;
    } catch (error) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats`, { withCredentials: true });
      setStats(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      return null;
    }
  }, []);

  const updateUser = useCallback(async (updates) => {
    try {
      const response = await axios.put(`${API}/user/profile`, updates, { withCredentials: true });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  }, []);

  const updateEnergy = useCallback(async (amount, reason) => {
    try {
      const response = await axios.post(`${API}/user/energy`, { amount, reason }, { withCredentials: true });
      setUser(prev => prev ? { ...prev, energy_level: response.data.energy_level } : null);
      return response.data.energy_level;
    } catch (error) {
      console.error('Failed to update energy:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setStats(null);
    }
  }, []);

  // Get age-specific styling
  const getAgeConfig = useCallback(() => {
    const configs = {
      explorador: {
        fontClass: 'font-kids',
        radius: 'rounded-[2rem]',
        buttonRadius: 'rounded-full',
        cardRadius: 'rounded-3xl',
        colorIntensity: 'vivid',
        mimoStyle: 'playful'
      },
      companero: {
        fontClass: 'font-teens',
        radius: 'rounded-2xl',
        buttonRadius: 'rounded-xl',
        cardRadius: 'rounded-2xl',
        colorIntensity: 'soft',
        mimoStyle: 'relaxed'
      },
      aliado: {
        fontClass: 'font-adults',
        radius: 'rounded-xl',
        buttonRadius: 'rounded-lg',
        cardRadius: 'rounded-xl',
        colorIntensity: 'minimal',
        mimoStyle: 'elegant'
      }
    };
    return configs[user?.age_mode] || configs.aliado;
  }, [user?.age_mode]);

  return (
    <UserContext.Provider value={{
      user,
      setUser,
      loading,
      setLoading,
      stats,
      fetchUser,
      fetchStats,
      updateUser,
      updateEnergy,
      logout,
      getAgeConfig
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
